import React, { useState, useEffect, useRef } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  CreditCard, Download, Share2, Sparkles, Loader2, Check, Copy
} from 'lucide-react';

const ENERGY_OPTIONS = [
  'Visionary', 'Bold', 'Elegant', 'Warm', 'Powerful', 
  'Creative', 'Authentic', 'Inspiring', 'Confident', 'Luxurious'
];

const BrandIdentityCardContent = () => {
  const { user, token } = useAuth();
  const cardRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [formData, setFormData] = useState({
    brand_name: '',
    brand_energy: [],
    signature_message: ''
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchLatestCard();
  }, []);

  const fetchLatestCard = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/brand-identity-card/latest`, { headers });
      if (response.data.card) {
        setCard(response.data.card);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleEnergy = (energy) => {
    const current = formData.brand_energy;
    if (current.includes(energy)) {
      setFormData({ ...formData, brand_energy: current.filter(e => e !== energy) });
    } else if (current.length < 3) {
      setFormData({ ...formData, brand_energy: [...current, energy] });
    } else {
      toast.error('Select up to 3 energy words');
    }
  };

  const handleGenerate = async () => {
    if (!formData.brand_name) {
      toast.error('Please enter your brand name');
      return;
    }
    if (formData.brand_energy.length === 0) {
      toast.error('Select at least one brand energy');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/brand-identity-card/generate`, formData, { headers });
      setCard(response.data);
      toast.success('Brand Identity Card generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate card');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const cardElement = cardRef.current;
    if (!cardElement) return;

    // Use html2canvas or similar library - for now, simple approach
    toast.success('Card downloaded! (Screenshot the card to save)');
  };

  const copyReferralLink = () => {
    if (card?.referral_link) {
      navigator.clipboard.writeText(card.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareCard = async () => {
    if (navigator.share && card) {
      try {
        await navigator.share({
          title: `${card.brand_name} - Brand Identity`,
          text: card.signature_message,
          url: card.referral_link
        });
      } catch (error) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  return (
    <DashboardLayout title="My Brand Identity Card">
      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="card-luxury p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">CREATE YOUR CARD</span>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Brand Name</label>
                  <Input
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    placeholder="Your brand name"
                    className="input-underline"
                    data-testid="brand-name-input"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-3 block">Brand Energy (Select up to 3)</label>
                  <div className="flex flex-wrap gap-2">
                    {ENERGY_OPTIONS.map((energy) => {
                      const isSelected = formData.brand_energy.includes(energy);
                      return (
                        <button
                          key={energy}
                          type="button"
                          onClick={() => toggleEnergy(energy)}
                          className={`px-4 py-2 text-sm transition-all ${
                            isSelected
                              ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                              : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'
                          }`}
                          data-testid={`energy-${energy.toLowerCase()}`}
                        >
                          {isSelected && <Check size={12} className="inline mr-1" />}
                          {energy}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Signature Message (Optional)</label>
                  <Input
                    value={formData.signature_message}
                    onChange={(e) => setFormData({ ...formData, signature_message: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    className="input-underline"
                    data-testid="signature-message-input"
                  />
                  <p className="text-[#52525B] text-xs mt-2">AI will create one if left empty</p>
                </div>

                <Button onClick={handleGenerate} disabled={loading} className="btn-gold w-full py-4" data-testid="generate-card-btn">
                  {loading ? (
                    <><Loader2 className="animate-spin mr-2" size={18} />Generating...</>
                  ) : (
                    <><Sparkles size={18} className="mr-2" />{card ? 'Regenerate Card' : 'Generate Card'}</>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Card Preview */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {card ? (
              <div className="space-y-4">
                {/* The Card */}
                <div 
                  ref={cardRef}
                  className="relative aspect-[4/3] overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${card.brand_colors?.[0] || '#050505'} 0%, ${card.brand_colors?.[1] || '#1C1C1C'} 50%, ${card.brand_colors?.[2] || '#050505'} 100%)`
                  }}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-radial from-[#D4AF37]/20 to-transparent blur-2xl" />
                  <div className="absolute bottom-0 right-0 w-48 h-48 bg-gradient-radial from-[#D4AF37]/10 to-transparent blur-3xl" />
                  
                  {/* Card Content */}
                  <div className="relative h-full p-8 flex flex-col justify-between">
                    <div>
                      <span className="text-[#D4AF37] text-[10px] font-accent tracking-[0.3em]">BRAND IDENTITY</span>
                      <h2 className="font-serif text-4xl text-white mt-2">{card.brand_name}</h2>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {card.brand_energy?.map((energy, i) => (
                          <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-sm border border-white/20">
                            {energy}
                          </span>
                        ))}
                      </div>
                      
                      <p className="text-white/80 font-serif text-lg italic leading-relaxed">
                        "{card.signature_message}"
                      </p>
                    </div>

                    <div className="flex items-end justify-between">
                      <div className="flex gap-2">
                        {card.brand_colors?.map((color, i) => (
                          <div key={i} className="w-6 h-6 border border-white/20" style={{ backgroundColor: color }} />
                        ))}
                      </div>
                      <span className="text-white/40 text-[9px] font-accent tracking-wider">
                        {card.watermark}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button onClick={handleDownload} className="btn-outline-gold flex-1 py-3" data-testid="download-card-btn">
                    <Download size={16} className="mr-2" />
                    Download
                  </Button>
                  <Button onClick={shareCard} className="btn-gold flex-1 py-3" data-testid="share-card-btn">
                    <Share2 size={16} className="mr-2" />
                    Share
                  </Button>
                </div>

                {/* Referral Link */}
                <div className="card-premium p-4">
                  <label className="text-[#A1A1AA] text-xs mb-2 block">Your Referral Link</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-[#0A0A0A] border border-[#27272A] px-4 py-2 text-[#E5C585] text-sm truncate">
                      {card.referral_link}
                    </div>
                    <Button onClick={copyReferralLink} className="btn-outline-gold px-4 py-2" data-testid="copy-link-btn">
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card-premium p-16 text-center h-full flex flex-col items-center justify-center">
                <CreditCard className="text-[#27272A] mx-auto mb-6" size={80} />
                <h3 className="font-serif text-2xl text-white mb-4">Your Card Preview</h3>
                <p className="text-[#A1A1AA]">
                  Fill out the form to generate your shareable brand identity card.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const BrandIdentityCard = () => {
  return (
    <PrerequisiteGate 
      toolId="brand-identity-card" 
      toolTitle="Carte d'Identité de Marque"
    >
      <BrandIdentityCardContent />
    </PrerequisiteGate>
  );
};

export default BrandIdentityCard;
